import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'

export const Route = createFileRoute('/_site/ai')({
  component: AiPage,
  head: () => ({
    meta: [
      { title: 'AI-native monitoring — let your agent run Uptrack' },
      {
        name: 'description',
        content:
          'Uptrack has a hosted MCP server. Claude or ChatGPT can list monitors, create checks, read incidents and check any URL from several regions when you ask.',
      },
      { property: 'og:title', content: 'AI-native monitoring — let your agent run Uptrack' },
      { property: 'og:image', content: 'https://uptrack.app/og/ai.png' },
      {
        property: 'og:description',
        content:
          'Connect Claude or ChatGPT to Uptrack over MCP and manage your monitoring in plain language.',
      },
    ],
    links: [{ rel: 'canonical', href: 'https://uptrack.app/ai' }],
  }),
})

const CAPABILITIES = [
  {
    title: 'Connect over MCP',
    body: 'Add Uptrack as a connector in Claude or ChatGPT once. It speaks the Model Context Protocol over Streamable HTTP, with OAuth — no API keys to paste, no glue code to write.',
  },
  {
    title: 'Ask, don’t click',
    body: '"Watch app.example.com and tell me if checkout breaks." Your agent creates the monitor, lists open incidents, acknowledges one to pause escalation, or pulls uptime — through real tools, scoped to your account.',
  },
  {
    title: 'Check any URL from a chat',
    body: 'The connector has a check_website tool. Give it any public URL and it checks the site from several regions right now, with no monitor needed. Screenshots and Core Web Vitals are not part of the connector. They stay in the dashboard and in the separate public browser-check tool.',
  },
  {
    title: 'Built to be read by machines',
    body: 'Uptrack scores Level 5 "Agent-Native" on isitagentready.com — an MCP server card, an A2A agent card, a discovery doc at /auth.md, an api-catalog, and AI rules in robots.txt. Agents can find and use it without a human in the loop.',
  },
]

function AiPage() {
  return (
    <>
      <section>
        <div className="container mx-auto px-4 py-12 md:py-16">
          <div className="max-w-3xl">
            <p className="mb-4 text-sm text-muted-foreground">AI-native monitoring</p>
            <h1 className="text-[2.25rem] font-semibold leading-[1.1] tracking-tight md:text-[3rem]">
              Let your AI agent run your monitoring
            </h1>
            <p className="mt-5 max-w-2xl text-lead text-body">
              Uptrack ships a hosted MCP server. Connect Claude or ChatGPT, and it can create monitors,
              check on incidents, and check any URL from several regions for you — in plain language, on your account.
              No scripts, no separate dashboard to babysit.
            </p>
          </div>

          {/* Connect snippet */}
          <div className="mt-10 max-w-3xl rounded-lg border border-border bg-card p-6">
            <p className="text-sm font-medium text-foreground">Add this connector in Claude or ChatGPT</p>
            <pre tabIndex={0} className="mt-3 overflow-x-auto rounded-md border border-border bg-background p-4 font-mono text-sm text-foreground">
              <code>https://api.uptrack.app/mcp</code>
            </pre>
            <p className="mt-3 text-[15px] text-body">
              It authenticates with OAuth on first use — approve once and your agent is connected. Tool
              access is scoped to your organization, same as the dashboard. You need an Uptrack account on a paid
              plan. Uptrack is also{' '}
              <a href="https://claude.ai/directory/uptrack" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-4">listed in Claude&rsquo;s connector directory</a>.
              Read the <Link to="/guides/mcp" className="text-primary underline underline-offset-4">connection guide</Link>.
            </p>
          </div>
        </div>
      </section>

      <section className="border-y border-border bg-card">
        <div className="container mx-auto px-4 py-12 md:py-[72px]">
          <ul className="grid grid-cols-1 gap-x-10 gap-y-8 md:grid-cols-2">
            {CAPABILITIES.map((c) => (
              <li key={c.title} className="border-l-2 border-border pl-5">
                <h2 className="text-[17px] font-semibold text-foreground">{c.title}</h2>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{c.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section>
        <div className="container mx-auto px-4 py-12 md:py-[72px]">
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="max-w-2xl">
              <h2 className="text-[1.75rem] font-semibold leading-tight tracking-tight md:text-headline">
                See plans — 50 monitors at 30-second checks from $3/mo
              </h2>
              <p className="mt-3 text-lead text-body">
                Create an account, then connect your agent. Or{' '}
                <a
                  href="https://isitagentready.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline underline-offset-4"
                >
                  see Uptrack&rsquo;s agent-readiness score
                </a>
                .
              </p>
            </div>
            <Button asChild size="lg" className="h-11 shrink-0 px-5 text-[15px]">
              <Link to="/signup">
                Get started <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  )
}
